import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@core/services/api/apiRequest";
import type { PublicProfile } from "../publicProfile.types";

/**
 * Arguments for useFetchOwnPublicProfile hook.
 */
export type UseFetchOwnPublicProfileArgs = {
  /** Program ID to scope the profile to */
  programId: number | undefined;
  /** Whether the query is enabled */
  isEnabled?: boolean;
  /** Current user (to determine if logged in) */
  user: { id: number } | null;
};

/**
 * Hook to fetch the current user's own public profile.
 * Returns React Query result with PublicProfile data.
 *
 * @example
 * ```typescript
 * const { data: profile, isLoading, error } = useFetchOwnPublicProfile({
 *   programId: 123,
 *   user,
 * });
 *
 * if (isLoading) return <Loader />;
 * if (!profile) return null;
 *
 * return <PublicProfileSheet profile={profile} />;
 * ```
 */
export function useFetchOwnPublicProfile({
  programId,
  isEnabled = true,
  user,
}: UseFetchOwnPublicProfileArgs) {
  return useQuery<PublicProfile, Error>({
    queryKey: ["own-public-profile", programId],
    queryFn: async () => {
      return (await apiRequest(
        `/profile/me/?program_id=${programId}`,
        "GET"
      )) as PublicProfile;
    },
    enabled: !!user && !!programId && isEnabled,
  });
}

export default useFetchOwnPublicProfile;
